import React from "react";
import Seo from "../components/Seo";
import { Section, Eyebrow, PageHero } from "../components/primitives";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import CTABanner from "../components/CTABanner";
import { motion } from "framer-motion";
import { Dumbbell, Apple, BatteryCharging, HeartPulse } from "lucide-react";

const pillars = [
  { icon: Dumbbell, title: "Training", weight: "Volume, intensity & consistency", value: 92 },
  { icon: Apple, title: "Nutrition", weight: "Macros, energy & fueling", value: 84 },
  { icon: BatteryCharging, title: "Recovery", weight: "Sleep, rest & readiness", value: 85 },
  { icon: HeartPulse, title: "Health", weight: "Body composition & vitals", value: 88 },
];

const DEMO_KINGDOM_SCORE = 87; // illustrative example score shown on the page
const RING_RADIUS = 88; // SVG radius for the large score ring

export default function KingdomScore() {
  const score = DEMO_KINGDOM_SCORE;
  const r = RING_RADIUS;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;

  return (
    <div>
      <Seo
        title="Kingdom Score"
        path="/kingdom-score"
        noindex
        description="The Kingdom Score is a single unified metric that combines your training, nutrition, recovery and health into one clear number."
      />
      <PageHero
        eyebrow="Kingdom Score"
        title="Your entire health,"
        highlight="in one number"
        description="The Kingdom Score is Jollof Fit Kingdom's signature metric — a unified score that combines training, nutrition, recovery and health into a single, clear measure of where you stand."
        eagle
      />

      <Section className="mt-4">
        <div className="grid items-center gap-16 lg:grid-cols-2">
          <Reveal>
            <div className="relative mx-auto flex h-72 w-72 items-center justify-center md:h-96 md:w-96">
              <div className="pointer-events-none absolute inset-0 rounded-full bg-gold/10 blur-3xl" />
              <svg className="h-full w-full -rotate-90" viewBox="0 0 200 200">
                <circle cx="100" cy="100" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
                <motion.circle
                  cx="100"
                  cy="100"
                  r={r}
                  fill="none"
                  stroke="url(#ksGrad)"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={c}
                  initial={{ strokeDashoffset: c }}
                  whileInView={{ strokeDashoffset: offset }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
                />
                <defs>
                  <linearGradient id="ksGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#f3e5ab" />
                    <stop offset="100%" stopColor="#aa8c2c" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-heading text-6xl font-semibold text-white md:text-7xl">{score}</span>
                <span className="mt-1 text-xs uppercase tracking-[0.3em] text-zinc-500">Kingdom Score</span>
                <span className="mt-3 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold">
                  Elite Tier
                </span>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <Eyebrow>How it's built</Eyebrow>
              <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-white md:text-4xl">
                Four pillars, one score
              </h2>
              <p className="mt-5 text-base leading-relaxed text-zinc-400">
                The Kingdom Score continuously analyzes the pillars of your health, weighting each pillar to
                produce a single number you can trust and act on.
              </p>
            </Reveal>
            <Stagger className="mt-8 space-y-4">
              {pillars.map((p) => (
                <StaggerItem key={p.title}>
                  <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <p.icon className="h-5 w-5 text-gold" strokeWidth={1.6} />
                        <div>
                          <p className="font-heading text-base text-white">{p.title}</p>
                          <p className="text-xs text-zinc-500">{p.weight}</p>
                        </div>
                      </div>
                      <span className="font-heading text-xl text-white">{p.value}</span>
                    </div>
                    <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                      <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-gold-light to-gold-dark"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${p.value}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </Section>

      <CTABanner
        title="One score. Total clarity."
        desc="Bring the Kingdom Score into your connected ecosystem with Jollof Fit Kingdom."
      />
    </div>
  );
}
